import { Icon } from "@/components/atoms/Icon/Icon";
import { useEffect, useRef, useState } from "react";
import "./SwitchButton.scss";

export type SwitchButtonValue = string | number;

export type SwitchButtonOption = {
  value: SwitchButtonValue;
  label?: string;
  iconName?: string;
  svgIcon?: React.ReactNode;
  disabled?: boolean;
};

export type SwitchButtonProps = {
  options: SwitchButtonOption[];
  value?: SwitchButtonValue;
  defaultValue?: SwitchButtonValue;
  onChange?: (value: SwitchButtonValue, option: SwitchButtonOption) => void;
  disabled?: boolean;
  className?: string;
  name?: string;
  ariaLabel?: string;
};

const getFirstEnabledOption = (options: SwitchButtonOption[], disabled?: boolean) =>
  options.find((option) => !disabled && !option.disabled);

const isSelectableOption = (option: SwitchButtonOption | undefined, disabled?: boolean) =>
  Boolean(option && !disabled && !option.disabled);

const getResolvedValue = (
  options: SwitchButtonOption[],
  candidate: SwitchButtonValue | undefined,
  disabled?: boolean,
) => {
  const option = options.find((item) => item.value === candidate);
  return isSelectableOption(option, disabled) ? candidate : getFirstEnabledOption(options, disabled)?.value;
};

/**
 * Segmented radio-style control with roving focus and optional hidden form value.
 * Disabled options are skipped for both keyboard navigation and fallback
 * selection.
 */
export const SwitchButton: React.FC<SwitchButtonProps> = ({
  options,
  value,
  defaultValue,
  onChange,
  disabled = false,
  className,
  name,
  ariaLabel,
}) => {
  const isControlled = value !== undefined;
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const [internalValue, setInternalValue] = useState<SwitchButtonValue | undefined>(() =>
    getResolvedValue(options, defaultValue, disabled),
  );

  const selectedValue = isControlled ? getResolvedValue(options, value, disabled) : internalValue;
  const selectedIndex = options.findIndex((option) => option.value === selectedValue);
  const focusableIndex =
    selectedIndex >= 0 ? selectedIndex : options.findIndex((option) => isSelectableOption(option, disabled));

  useEffect(() => {
    if (!isControlled) {
      setInternalValue((currentValue) => getResolvedValue(options, currentValue, disabled));
    }
  }, [disabled, isControlled, options]);

  const selectOption = (option: SwitchButtonOption) => {
    if (!isSelectableOption(option, disabled) || option.value === selectedValue) {
      return;
    }

    if (!isControlled) {
      setInternalValue(option.value);
    }

    onChange?.(option.value, option);
  };

  const moveSelection = (currentIndex: number, direction: 1 | -1) => {
    for (let offset = 1; offset <= options.length; offset += 1) {
      const nextIndex = (currentIndex + offset * direction + options.length) % options.length;
      const nextOption = options[nextIndex];

      if (isSelectableOption(nextOption, disabled)) {
        selectOption(nextOption);
        buttonRefs.current[nextIndex]?.focus();
        return;
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        moveSelection(index, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        moveSelection(index, -1);
        break;
      case "Home": {
        event.preventDefault();
        const firstEnabledIndex = options.findIndex((option) => isSelectableOption(option, disabled));

        if (firstEnabledIndex >= 0) {
          selectOption(options[firstEnabledIndex]);
          buttonRefs.current[firstEnabledIndex]?.focus();
        }
        break;
      }
      case "End": {
        event.preventDefault();
        const reversedIndex = [...options].reverse().findIndex((option) => isSelectableOption(option, disabled));

        if (reversedIndex >= 0) {
          const lastEnabledIndex = options.length - 1 - reversedIndex;
          selectOption(options[lastEnabledIndex]);
          buttonRefs.current[lastEnabledIndex]?.focus();
        }
        break;
      }
      default:
        break;
    }
  };

  const classes = ["switch-button", disabled ? "switch-button--disabled" : "", className].filter(Boolean).join(" ");

  return (
    <div className={classes} role="radiogroup" aria-label={ariaLabel} aria-disabled={disabled}>
      {options.map((option, index) => {
        const isActive = option.value === selectedValue;
        const isDisabled = disabled || option.disabled;

        return (
          <button
            key={option.value}
            ref={(element) => {
              buttonRefs.current[index] = element;
            }}
            type="button"
            className={[
              "switch-button__option",
              isActive ? "switch-button__option--active" : "",
              isDisabled ? "switch-button__option--disabled" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            role="radio"
            aria-checked={isActive}
            aria-disabled={isDisabled}
            tabIndex={index === focusableIndex ? 0 : -1}
            disabled={isDisabled}
            onClick={() => selectOption(option)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            {(option.iconName || option.svgIcon) && (
              <Icon
                className={["switch-button__icon", option.svgIcon ? "switch-button__icon--svg" : ""]
                  .filter(Boolean)
                  .join(" ")}
                iconName={option.iconName}
                svgIcon={option.svgIcon}
                aria-hidden="true"
              />
            )}
            {option.label && <span className="switch-button__label">{option.label}</span>}
          </button>
        );
      })}
      {name && selectedValue !== undefined && <input type="hidden" name={name} value={String(selectedValue)} />}
    </div>
  );
};
